import { useEffect, useState } from "react";

/**
 * Reactive boolean for a CSS media query. SSR-safe: returns the optional
 * `defaultValue` (false by default) on first render, then syncs after mount.
 *
 *   const isDesktop = useMediaQuery("(min-width: 1024px)");
 *   const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
 *
 * Adapted from usehooks-ts (MIT).
 */
export function useMediaQuery(
  query: string,
  defaultValue = false,
): boolean {
  const [matches, setMatches] = useState<boolean>(defaultValue);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    setMatches(mq.matches);

    if ("addEventListener" in mq) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    // Safari < 14 / older browsers
    (mq as MediaQueryList).addListener(handler);
    return () => (mq as MediaQueryList).removeListener(handler);
  }, [query]);

  return matches;
}
